import React, { Fragment, useEffect, useState } from 'react';
import { Col, Container, Input, Label, Row } from 'reactstrap';
import { toast } from "react-toastify";
import { ServiceThreadSpinner } from '.';
import { api, helpers } from '../utils';
import _ from "lodash";

const YARDS_FACTOR = 4464528;
const MINS_PER_HOUR = 60;
const METERS_PER_YD = 1.09361;

export default function DoublingCalcs() {
   const [model, setDefaults] = useState({});
   const [spin, setSpinner] = useState(true);
   const [useYPM, setUseYPM] = useState(true);
   const [metersPerMinute, setMPM] = useState(0);
   const denier = model?.baseYarn * model?.ply ?? 0;
   const yardsPerPound = denier > 0 ? (YARDS_FACTOR / denier) : 0;

   // dependent on yardsPerPound
   const metersPerPound = yardsPerPound > 0 ? (yardsPerPound / METERS_PER_YD) : 0;
   const pphPerSpindle = (model?.speed * MINS_PER_HOUR / yardsPerPound) ?? 0;
   const expectedPPH = (model?.efficiency * pphPerSpindle) ?? 0;
   const pph = (model?.spindles * expectedPPH) ?? 0;

   useEffect(() => {
      GetDoublerDefaults();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [])

   useEffect(() => {
      if (model?.speed > 0) {
         // calculate meters per min
         const x = parseInt(model.speed);
         let mpm = (x / METERS_PER_YD).toFixed(0);
         setMPM(mpm);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [model])

   function GetDoublerDefaults() {
      setSpinner(true);
      api
         .fetch(`Engineering/GetDoublerDefaults`)
         .then((response) => {
            if (response?.data) {
               if (response.data.success) {
                  setDefaults(response.data.defaults);
               } else {
                  toast.error(response.data.message);
               }
            }
            else {
               toast.error("Unable to retrieve default values!");
            }
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error(error);
         })
         .finally(() => {
            setSpinner(false);
         });
   }

   const handleFocus = (event) => event.target.select();

   function onChange(field, value) {
      if (field === "baseYarn" && value > 99999) {
         toast.warn("Base yarn cannot exceed 5 digits");
         value = 99999;
      }
      if (field === "ply" && value > 99) {
         toast.warn("Ply cannot exceed 2 digits");
         value = 99;
      }
      if (field === "spindles" && value > 999) {
         toast.warn("Spindles cannot exceed 3 digits");
         value = 999;
      }

      let changes = Object.assign({}, model);
      changes[field] = value;
      setDefaults(changes);
   }

   function onChangeMPM(value) {
      // Meters per Minute
      if (_.isNil(value) || value === "") value = 1;
      if (value === "" || value < 1) value = 1;
      const x = parseInt(value);
      setMPM(x);

      // calculate yards per min
      let ypm = (x * METERS_PER_YD).toFixed(0);

      // save ypm to the model
      let changes = Object.assign({}, model);
      changes.speed = ypm;
      setDefaults(changes);
   }

   function onChangeYPM(value) {
      // Yards per Minute
      if (_.isNil(value) || value === "")
         value = 1;
      else if (value === "" || value < 1)
         value = 1;
      else if (value > 99999) {
         toast.warn("YPM cannot exceed 5 digits");
         value = 99999;
      }

      // calculate meters per min
      const x = parseInt(value);
      let mpm = (x / METERS_PER_YD).toFixed(0);
      setMPM(mpm);

      // save ypm to the model
      let changes = Object.assign({}, model);
      changes.speed = x;
      setDefaults(changes);
   }

   return (
      <Container fluid>
         <Row>
            <Col>
               <Fragment>
                  <ServiceThreadSpinner on={spin}>
                     <Row className="px-2 py-2">
                        <Col xxl={{ offset: 0, size: 12 }}>
                           <Label className="noWrap h5">Enter appropriate values below to see calculation results</Label>
                           <br /><br />
                           <div>
                              {/* Use YPM Check Box */}
                              <Row className="h-100">
                                 <Col className="form-check form-check-inline noWrap mx-3 px-0 h6 width200px">
                                    <Input
                                       className="mx-0"
                                       type="checkbox"
                                       name="useYPM"
                                       checked={useYPM}
                                       onChange={(e) => setUseYPM(!useYPM)}
                                    />
                                    <Label for="useYPM">&nbsp;&nbsp;&nbsp;&nbsp;Enter Speed in YPM</Label>
                                 </Col>
                              </Row>

                              {/* Denier and Ply Entry Fields plus Total Denier value */}
                              <Row className="h-100">
                                 <Col className="d-flex align-items-center noWrap h6 width125px">Base Yarn (Dn):</Col>
                                 <Col className="data-cell noWrap h6 width135px">
                                    <Input
                                       type="number"
                                       name="baseYarn"
                                       value={model?.baseYarn || "0"}
                                       className="h-100 py-1 no-spinner"
                                       onChange={(e) => { onChange("baseYarn", e.target.value) }}
                                       maxLength={5}
                                       max={99999}
                                       min={1}
                                       onFocus={handleFocus}
                                    />
                                 </Col>
                                 <Col className="width40px"></Col>
                                 <Col className="d-flex align-items-center noWrap h6 width95px">
                                    Ply:
                                 </Col>
                                 <Col className="data-cell noWrap h6 width80px">
                                    <Input
                                       type="number"
                                       name="ply"
                                       value={model?.ply || "0"}
                                       className="h-100 py-1 no-spinner"
                                       onChange={(e) => { onChange("ply", e.target.value) }}
                                       maxLength={2}
                                       max={99}
                                       min={1}
                                       onFocus={handleFocus}
                                    />
                                 </Col>
                                 <Col className="width40px"></Col>
                                 <Col className="d-flex align-items-center noWrap h6 width100px">
                                    Total Denier:
                                 </Col>
                                 <Col className="d-flex align-items-center noWrap h6 width80px">
                                    {helpers.formatNumberWithThousands(denier)}
                                 </Col>
                              </Row>

                              {/* Efficiency Rate and Spindle Entry Fields */}
                              <Row className="h-100">
                                 <Col className="d-flex align-items-center noWrap h6 width125px">Efficiency:</Col>
                                 <Col className="data-cell noWrap h6 width135px">
                                    <Input
                                       type="number"
                                       name="efficiency"
                                       value={model?.efficiency || "0"}
                                       className="h-100 py-1 no-spinner"
                                       onChange={(e) => { onChange("efficiency", e.target.value) }}
                                       maxLength={4}
                                       max={1.00}
                                       min={0.10}
                                       onFocus={handleFocus}
                                    />
                                 </Col>
                                 <Col className="width40px"></Col>
                                 <Col className="d-flex align-items-center noWrap h6 width95px">Spindles:</Col>
                                 <Col className="data-cell noWrap h6 width80px">
                                    <Input
                                       type="number"
                                       name="spindles"
                                       value={model?.spindles || "0"}
                                       className="h-100 py-1 no-spinner"
                                       onChange={(e) => { onChange("spindles", e.target.value) }}
                                       maxLength={3}
                                       max={999}
                                       min={1}
                                       onFocus={handleFocus}
                                    />
                                 </Col>
                              </Row>

                              {/* YPM Speed Entry Fields plus Yards per Pound */}
                              <Row className="h-100">
                                 <Col className="d-flex align-items-center noWrap h6 width125px">Speed (YPM):</Col>
                                 {useYPM ? (
                                    <Col className="data-cell noWrap h6 width135px">
                                       <Input
                                          type="number"
                                          name="speed"
                                          value={model?.speed || "0"}
                                          className="h-100 py-1 no-spinner"
                                          onChange={(e) => { onChangeYPM(e.target.value) }}
                                          maxLength={5}
                                          max={99999}
                                          min={1}
                                          onFocus={handleFocus}
                                       />
                                    </Col>
                                 ) : <Col className="data-cell h-100 noWrap h6 width135px">
                                       &nbsp;&nbsp;{helpers.formatNumberWithThousands(model?.speed)}
                                    </Col>
                                 }
                                 <Col className="width40px"></Col>
                                 <Col className="d-flex align-items-center noWrap h6 width95px">Yds/Lb:</Col>
                                 <Col className="d-flex align-items-center noWrap h6 width80px">
                                    {helpers.formatNumberWithThousands(yardsPerPound, 2)}
                                 </Col>
                              </Row>

                              {/* MPM Speed Entry Fields plus Meters per Pound */}
                              <Row className="h-100">
                                 <Col className="d-flex align-items-center noWrap h6 width125px">Speed (MPM):</Col>
                                 {!useYPM ? (
                                    <Col className="data-cell noWrap h6 width135px">
                                       <Input
                                          type="number"
                                          name="mpm"
                                          value={metersPerMinute || "0"}
                                          className="h-100 py-1 no-spinner"
                                          onChange={(e) => { onChangeMPM(e.target.value) }}
                                          maxLength={5}
                                          max={99999}
                                          min={1}
                                          onFocus={handleFocus}
                                       />
                                    </Col>
                                 ) : <Col className="data-cell h-100 noWrap h6 width135px">
                                       &nbsp;&nbsp;{helpers.formatNumberWithThousands(metersPerMinute)}
                                    </Col>
                                 }
                                 <Col className="width40px"></Col>
                                 <Col className="d-flex align-items-center noWrap h6 width95px">
                                    Meters/Lb:
                                 </Col>
                                 <Col className="d-flex align-items-center noWrap h6 width80px">
                                    {helpers.formatNumberWithThousands(metersPerPound, 2)}
                                 </Col>
                              </Row>
                              <br />

                              {/* Non-Stop PPH per Spindle */}
                              <Row>
                                 <Col className="data-cell noWrap h5 width225px">
                                    Non-Stop PPH/Spindle:
                                 </Col>
                                 <Col className="data-cell noWrap h5">
                                    {helpers.formatNumberWithThousands(pphPerSpindle, 2)}
                                 </Col>
                              </Row>

                              {/* Expected PPH per Spindle */}
                              <Row className="h-100 py-1">
                                 <Col className="data-cell noWrap h5 width225px">
                                    Expected PPH/Spindle:
                                 </Col>
                                 <Col className="data-cell noWrap h5">
                                    {helpers.formatNumberWithThousands(expectedPPH, 2)}
                                 </Col>
                              </Row>

                              {/* Total PPH */}
                              <Row>
                                 <Col className="data-cell noWrap h5 width225px">
                                    Lbs/Hr:
                                 </Col>
                                 <Col className="data-cell noWrap h5">
                                    {helpers.formatNumberWithThousands(pph, 2)}
                                 </Col>
                              </Row>
                           </div>
                        </Col>
                     </Row>
                  </ServiceThreadSpinner>
               </Fragment>
            </Col>
         </Row>
      </Container>
   );
}
